import { validateCardNumber } from './creditCardValidator';
import {
  DateInput,
  DateTimeInput,
  DATE_CHANGE_ERROR_INVALID,
  DATE_CHANGE_ERROR_OUT_OF_RANGE
} from '@cvent/nucleus-core-datetime-utils';

/**
 * Validates whether the value exists or not. The value must not be empty or completely white-space.
 */
export const requiredValidator = value => {
  return !!value && (typeof value !== 'string' || !!value.trim());
};

/**
 * Validates if a string is at least as long as the the specified length.
 * @param length The minimum length the string can be.
 */
export const minLengthValidator = length => value => {
  return requiredValidator(value) && value.trim().length >= length;
};

/**
 * Validates if a string is longer than the the specified length.
 * @param length The maximum length the string can be.
 */
export const maxLengthValidator = length => value => {
  return requiredValidator(value) && value.trim().length <= length;
};

/**
 * Validates if the text only contains digits
 * Accepts -3 -.3 -3. -3.0 and all the positive variants
 * @param text The string to validate
 */
const isSupportedNumber = text => {
  const pattern = /(^[-]?([0-9.]+)([0-9.]+)?)$/;
  return pattern.test(text);
};

/**
 * Validates if a string is a number within a certain range
 * @param min The minimum numeric value
 * @param max The maximum numeric value
 */
export const numberValidator = (min, max) => value => {
  if (!requiredValidator(value)) {
    return false;
  }
  const numValue = Number(value.trim());
  return (
    isSupportedNumber(value) &&
    !value.includes('.') &&
    (typeof min !== 'number' || numValue >= min) &&
    (typeof max !== 'number' || numValue <= max)
  );
};

/**
 * Validates if a string is a decimal value
 */
export const decimalValidator = value => {
  // Check for 0 or 1 decimal points
  return (
    requiredValidator(value) && isSupportedNumber(value) && isFinite(value)
  );
};

/**
 * Validates if a string is a currency value i.e. if it is a non-negative decimal
 */
export const currencyValidator = value => {
  // Check for optional groups of three separated by a comma (or decimal for Euro),
  // followed by an optional group of a decimal (or comma for Euro) followed by  0 - 2 numbers
  const currencyFormat = /^[1-9]\d*(((,\d{3})*)?(\.\d{0,2})?)$/;
  const euroFormat = /^[1-9]\d*(((\.\d{3})*)?(,\d{0,2})?)$/;
  return (
    requiredValidator(value) &&
    (currencyFormat.test(value) || euroFormat.test(value))
  );
};

/**
 * Validates if a value is a US Phone Number (999-999-9999)
 */
export const usPhoneNumberValidator = value => {
  return requiredValidator(value) && /^\s*\d{3}-\d{3}-\d{4}\s*$/.test(value);
};

/**
 * Validates if a value is a Social Security Number (123-45-6789)
 */
export const socialSecurityNumberValidator = value => {
  return requiredValidator(value) && /^\s*\d{3}-\d{2}-\d{4}\s*$/.test(value);
};

/**
 * Verfies the email address matches a basic format. It is understood that this
 * validation does not cover every aspect of the email format which should
 * be handled server side.
 */
const validEmailRegex = /^.+@.+\..+$/u;
export const emailFormatValidator = value => {
  return requiredValidator(value) && validEmailRegex.test(value.trim());
};

export function optionalValidator(validator) {
  return (value, additionalInformation) => {
    return requiredValidator(value, additionalInformation)
      ? validator(value, additionalInformation)
      : true;
  };
}

/**
 * Verifies that a given value is a valid credit format based on the credit card type
 */
export const creditCardValidator = cardType => value => {
  return requiredValidator(value) && validateCardNumber(value, cardType);
};

export function dateRequiredValidator(dateInput) {
  if (typeof dateInput === 'string' || dateInput instanceof Date) {
    return true;
  } else if (
    dateInput instanceof DateInput ||
    dateInput instanceof DateTimeInput
  ) {
    return dateInput.dateText && dateInput.dateText.trim();
  }
  return false;
}

export function dateFormatValidator(dateInput) {
  return !(
    dateInput instanceof DateInput &&
    dateInput.statusCode === DATE_CHANGE_ERROR_INVALID
  );
}

export function dateRangeValidator(dateInput) {
  return !(
    dateInput instanceof DateInput &&
    dateInput.statusCode === DATE_CHANGE_ERROR_OUT_OF_RANGE
  );
}

export function timeFormatValidator(dateInput, useSeconds) {
  if (!(dateInput instanceof DateInput && dateInput.date)) {
    return true;
  }
  if (!dateInput.timeText) {
    return false;
  }
  const { hour, minute, second } = dateInput.timeText;
  return (
    typeof hour === 'number' &&
    typeof minute === 'number' &&
    (!useSeconds || typeof second === 'number')
  );
}
